import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AlertDTO, AlertListDTO, AlertListQuery, AlertsSummaryDTO } from '@lightbm/shared';
import { api } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';

export type AlertFilters = Omit<AlertListQuery, 'cursor' | 'limit'>;

export const useAlerts = (filters: AlertFilters, enabled = true) =>
  useInfiniteQuery({
    enabled,
    queryKey: queryKeys.alertList(filters),
    queryFn: ({ pageParam, signal }) =>
      api.get<AlertListDTO>('/alerts', { ...filters, cursor: pageParam, limit: 30 }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

/** The bell and the Alerts badge: polled, so new alerts show up without a reload (ALR-02). */
export const useAlertsSummary = (enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.alertsSummary,
    queryFn: () => api.get<AlertsSummaryDTO>('/alerts/summary'),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

function useRefreshAlerts() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.alerts });
    void queryClient.invalidateQueries({ queryKey: queryKeys.analytics });
  };
}

export function useMarkAlertRead() {
  const refresh = useRefreshAlerts();
  return useMutation({
    mutationFn: (id: string) => api.post<AlertDTO>(`/alerts/${id}/read`),
    onSuccess: refresh,
  });
}

export function useMarkAllAlertsRead() {
  const queryClient = useQueryClient();
  const refresh = useRefreshAlerts();
  return useMutation({
    mutationFn: () => api.post<AlertsSummaryDTO>('/alerts/read-all'),
    onSuccess: (summary) => {
      queryClient.setQueryData(queryKeys.alertsSummary, summary);
      refresh();
    },
  });
}
